import type { ElementNodeDto, ElementType } from '@xshop/shared';
import { markRect } from './editorMeasure';
import { scrollMarkIntoView } from './editorReveal';

// The element tree, as the page itself carries it. The renderer marks every node it draws with the node's
// id, its type, what the catalog says may go inside it and — for a node that holds plain words — the prop
// those words belong in. Everything the canvas knows about depth it reads back off those four attributes,
// so a click resolves to the innermost node under the pointer without the preview keeping a second copy of
// the merchant's tree, and the drop rules stay the catalog's rather than becoming a second list here.

/** The attribute the renderer marks every drawn node with; the builder finds a node by this and nothing else. */
export const ELEMENT_ATTRIBUTE = 'data-xshop-el';

/** What the node is, so the toolbar can name what is selected without a lookup back into the tree. */
export const ELEMENT_TYPE_ATTRIBUTE = 'data-xshop-el-type';

/** The name the merchant or the block gave the node, so the canvas tag says "Hero" and not "Section". */
export const ELEMENT_NAME_ATTRIBUTE = 'data-xshop-el-name';

/** What may go inside: `*` for anything, a space-separated list of types, absent for a node that takes none. */
export const ELEMENT_ACCEPTS_ATTRIBUTE = 'data-xshop-el-accepts';

/** The prop a merchant's typing is written to, present only on a node whose content is plain words. */
export const ELEMENT_TEXT_ATTRIBUTE = 'data-xshop-el-text';

/** The prop a `heading` or a `paragraph` carries its words in, unless its renderer names another one. */
export const ELEMENT_TEXT_PROP = 'text';

const MARK = `[${ELEMENT_ATTRIBUTE}]`;

/** Past this many accepted types a list is spelled `*`, so a generic box does not carry sixty names. */
const ANY_ELEMENT_COUNT = 40;

// Names the innermost drawn node under a point, which is what a click at any depth selects.
export const elementAt = (target: EventTarget | null): string | null => {
  if (!(target instanceof Element)) return null;
  return target.closest(MARK)?.getAttribute(ELEMENT_ATTRIBUTE) ?? null;
};

// Finds the mark one node was drawn under, which is the only handle the canvas ever has on it.
export const elementMark = (id: string): Element | null =>
  document.querySelector(`[${ELEMENT_ATTRIBUTE}="${CSS.escape(id)}"]`);

// Names the node one mark is, for a walk that already has the mark in hand.
export const elementIdOf = (mark: Element): string | null => mark.getAttribute(ELEMENT_ATTRIBUTE);

// Measures the box one node occupies, which is the box the renderer drew it in.
export const elementBox = (id: string): DOMRect | null => markRect(elementMark(id));

// Names the node one node sits directly inside — a real step up the tree, never a jump to the section.
export const parentElementId = (id: string): string | null => {
  const parent = elementMark(id)?.parentElement?.closest(MARK);
  return parent ? elementIdOf(parent) : null;
};

// Names what type one drawn node is, for chrome that has to say what the merchant has selected.
export const elementTypeOf = (id: string): string | null =>
  elementMark(id)?.getAttribute(ELEMENT_TYPE_ATTRIBUTE) ?? null;

// Names what the merchant or its block called one drawn node, or null when it carries no name of its own.
export const elementNameOf = (id: string): string | null =>
  elementMark(id)?.getAttribute(ELEMENT_NAME_ATTRIBUTE) || null;

// Names the prop a double-click on one node types into, or null when it holds no plain words of its own.
export const elementTextProp = (id: string): string | null =>
  elementMark(id)?.getAttribute(ELEMENT_TEXT_ATTRIBUTE) || null;

// Reports whether the page carries an element tree at all, so a page of bands behaves exactly as it did.
export const pageHasElements = (): boolean => document.querySelector(MARK) !== null;

// Names the nodes drawn directly inside one mark, in the order the renderer drew them.
export const childMarks = (mark: Element): readonly Element[] =>
  Array.from(mark.querySelectorAll(MARK)).filter((child) => child.parentElement?.closest(MARK) === mark);

/**
 * The nodes the page drew at its top level, in order — the tree's own roots, read back off the page.
 * These are the bands a section is added between, and on a rebuilt page they are the only honest
 * answer: a root still drawing a theme's band carries that band's own section mark inside itself, so
 * counting section marks there would count the insides of the list rather than the list.
 */
export const rootElementMarks = (): readonly Element[] =>
  Array.from(document.querySelectorAll(MARK)).filter((mark) => !mark.parentElement?.closest(MARK));

// Names where one node sits among its siblings, or -1 when it is a root the page drew on its own.
export const childIndexOf = (id: string): number => {
  const mark = elementMark(id);
  const parent = mark?.parentElement?.closest(MARK);
  return mark && parent ? childMarks(parent).indexOf(mark) : -1;
};

// Reports whether one node holds another anywhere below it, so nothing can be dropped inside itself.
export const containsElement = (ancestorId: string, id: string): boolean => {
  const ancestor = elementMark(ancestorId);
  const mark = elementMark(id);
  return Boolean(ancestor && mark && ancestor.contains(mark));
};

// Reports whether one node will take a carried type inside it, reading what the renderer stamped for it.
export const acceptsChild = (mark: Element, type: string | null): boolean => {
  const accepts = mark.getAttribute(ELEMENT_ACCEPTS_ATTRIBUTE);
  if (!accepts) return false;
  return accepts === '*' || type === null || accepts.split(' ').includes(type);
};

// The deepest node under a point that will take the carried type, climbing a level at a time until one will.
export const containerAt = (target: Element | null, type: string | null): Element | null => {
  let mark = target?.closest(MARK) ?? null;
  while (mark && !acceptsChild(mark, type)) mark = mark.parentElement?.closest(MARK) ?? null;
  return mark;
};

// Brings one node into view, for a merchant who picked it out of the builder's tree rather than the page.
export const revealElement = (id: string): void => {
  document.dispatchEvent(new CustomEvent('xshop:reveal-element', { detail: id }));
  window.requestAnimationFrame(() => {
    const mark = elementMark(id);
    scrollMarkIntoView(mark && mark.getBoundingClientRect().height > 0 ? mark : mark?.firstElementChild);
  });
};

/**
 * Names one node type the way a merchant reads it — `product-price` is "Product price". Derived rather
 * than looked up: `elements/catalog/` holds the real label beside seventy definitions of default props
 * and styles, and pulling that into the module every visit imports would put the whole library in a
 * shopper's bundle to spell one word in the builder's toolbar.
 */
export const elementTypeLabel = (type: string): string =>
  type.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

/** What one drawn node wears in the builder, and what a shopper's page is never given any of. */
export type ElementMarkAttrs = Readonly<Record<string, string>>;

// Spells what the catalog says goes inside one node, so the canvas reads back one string and not a list.
const acceptsValue = (accepts: readonly ElementType[], anyOf: number): string | null =>
  accepts.length === 0 ? null : (accepts.length >= anyOf ? '*' : accepts.join(' '));

/**
 * The attributes the renderer stamps on a node it draws inside the builder. Built here rather than in
 * the renderer so that the names, and what they mean, live beside the code that reads them back — the
 * same arrangement `SectionMark` and `FieldMark` already have with the themes that draw them.
 */
export function elementMarkAttrs(node: ElementNodeDto, accepts: readonly ElementType[], textProp: string | null): ElementMarkAttrs {
  const takes = acceptsValue(accepts, ANY_ELEMENT_COUNT);
  return {
    [ELEMENT_ATTRIBUTE]: node.id,
    [ELEMENT_TYPE_ATTRIBUTE]: node.type,
    ...(node.name ? { [ELEMENT_NAME_ATTRIBUTE]: node.name } : {}),
    ...(takes ? { [ELEMENT_ACCEPTS_ATTRIBUTE]: takes } : {}),
    ...(textProp ? { [ELEMENT_TEXT_ATTRIBUTE]: textProp } : {})
  };
}
